import type { PetChatReplay as PetChatReplayData } from "@/shared/contracts";
import { Button, Chip, EmptyState, LoadingState } from "@/shared/ui";
import { PetPortrait } from "@/features/pets/PetPortrait";
import { chatEndingLabel, chatStateOf } from "../petFriends";
import { petChatTrouble } from "./PetChatList";
import { chatTimeText } from "./usePetChats";
import "./petChats.css";

/**
 * 一场聊天的回放（P2，6796 起草；只读，主人旁观，不能插话；路由与接线归 6c2b）。
 * - 两边气泡分左右：TA 在右，朋友在左；只有这只宠物看得见的那几句（契约口径），按先后；
 * - 契约里每句没有时刻、回放也没有游标：顶上写这一场什么时候开始（看的人的本地时间，同一天只写几点），整场一次读完；
 * - 底部一句「这是 TA 和朋友的悄悄话，你在旁边看着」，**不放输入框、不放任何发送入口**；
 * - 空、读失败（可重试）、能力未接入，各一句温和的话，不露码。
 */
export function PetChatReplay({ petName, replay, loading, error, onRetry, now = new Date() }: {
  petName: string | null;
  replay: PetChatReplayData | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  now?: Date;
}) {
  const name = petName || "TA";
  if (loading) return <LoadingState label={`正在翻 ${name} 和朋友的聊天…`} lines={4} />;
  if (error || !replay) {
    const trouble = petChatTrouble(error, name);
    return <EmptyState icon="comment" title={trouble.title} action={trouble.retry ? <Button variant="secondary" size="sm" onClick={onRetry}>再试一次</Button> : undefined} />;
  }
  const friend = replay.counterpart.name;
  const state = chatStateOf(replay);
  const ending = chatEndingLabel(replay);
  const started = chatTimeText(replay.started_at, now, true);
  return (
    <div className="ps-pc-replay">
      <header className="ps-pc-replay__head">
        <PetPortrait name={friend} species={replay.counterpart.species} size={40} />
        <div className="ps-pc-replay__who">
          <strong className="ps-pc-replay__name">{name} 和 {friend}</strong>
          <span className="ps-pc-replay__meta">
            {started ? <span>{started} 开始</span> : null}
            {state ? <Chip tone={state.tone}>{state.label}</Chip> : null}
          </span>
        </div>
      </header>
      {replay.lines.length ? (
        <ol className="ps-pc-replay__lines" aria-label={`${name} 和 ${friend} 说的话`}>
          {replay.lines.map((line) => (
            <li key={line.seq} className={`ps-pc-bubble ${line.speaker === "mine" ? "is-mine" : "is-theirs"}`}>
              <span className="visually-hidden">{line.speaker === "mine" ? name : friend}：</span>
              {line.text}
            </li>
          ))}
        </ol>
      ) : (
        <p className="ps-pc-replay__empty">这一场还没有能看的话。</p>
      )}
      {ending ? <p className="ps-pc-replay__ending">{ending}</p> : null}
      <p className="ps-pc-replay__note">这是 {name} 和朋友的悄悄话，你在旁边看着。</p>
    </div>
  );
}
